/**
 * MySchedule.js — Student weekly schedule with print + PDF export
 * ────────────────────────────────────────────────────────────────
 * User mandate: "Students need a clear understanding of what classes
 * they need to attend. They should have the option to print out a
 * hard copy of their schedule for offline viewing. The school should
 * be able to generate the schedule for the student."
 *
 * Features:
 *   • Weekly grid, color-coded: gold = Zion Pillar, blue = Academic
 *   • Grade selector — students see current placement by default;
 *     admin can view any student's schedule (targetUserId prop)
 *   • Print button — native window.print with print-only CSS
 *   • Download PDF button — reuses pdfExport.js utility
 *   • "Test out of a grade" CTA if test_out_available
 *   • "Enroll in Ordination Track" toggle
 */
import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_COLOR = {
  pillar:   { bg: "#3a2e0a", border: "#FFD700", text: "#FFD700", label: "Zion Pillar" },
  academic: { bg: "#0a1f3a", border: "#4B9EFF", text: "#87CEEB", label: "Academic Foundations" },
};

export default function MySchedule({ user, isAdmin = false, targetUserId = null }) {
  const [data, setData] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ordinationStatus, setOrdinationStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const printRef = useRef(null);

  const fetchSchedule = useCallback(async (grade) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      const params = {};
      if (grade) params.grade = grade;
      if (isAdmin && targetUserId) params.user_id = targetUserId;
      const { data: res } = await axios.get(`${API}/enrollment/schedule`, {
        params,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(res);
      // Only anchor the selector to the server's current grade the FIRST
      // time (when the user hasn't picked anything yet). Never overwrite
      // an explicit user selection here — that caused the selector to
      // snap back to the old value after a change.
      setSelectedGrade((prev) => prev || grade || res.current_grade);
    } catch (e) {
      setError(e?.friendlyMessage || e?.message || "Couldn't load schedule");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, targetUserId]);

  const fetchOrdination = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const { data: res } = await axios.get(`${API}/enrollment/ordination-status`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setOrdinationStatus(res);
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => { fetchSchedule(null); fetchOrdination(); }, [fetchSchedule, fetchOrdination]);

  const changeGrade = (g) => { setSelectedGrade(g); fetchSchedule(g); };

  const handlePrint = () => window.print();

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    setBusy(true);
    try {
      const html2pdfMod = await import("html2pdf.js");
      const html2pdf = html2pdfMod.default || html2pdfMod;
      await html2pdf()
        .from(printRef.current)
        .set({
          filename: `YLC_Schedule_${data?.target_grade || "K"}_${(user?.username || "student")}.pdf`,
          margin: [12, 12, 12, 12],
          html2canvas: { scale: 2, backgroundColor: "#ffffff" },
          jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        })
        .save();
    } catch (e) {
      alert("PDF export failed: " + (e?.message || "unknown"));
    } finally {
      setBusy(false);
    }
  };

  const toggleOrdination = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const endpoint = ordinationStatus?.opted_in
        ? "/enrollment/ordination-opt-out"
        : "/enrollment/ordination-opt-in";
      await axios.post(`${API}${endpoint}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      fetchOrdination();
    } catch (e) {
      alert(e?.friendlyMessage || "Couldn't update ordination status");
    } finally { setBusy(false); }
  };

  if (loading && !data) return <div style={{ padding: 30, color: "#888" }}>Loading schedule…</div>;
  if (error && !data) return <div style={{ padding: 30, color: "#ff6b6b" }}>{error}</div>;
  if (!data) return null;

  return (
    <div data-testid="my-schedule" style={{ padding: "16px 20px" }}>
      {/* Print-only style */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #schedule-printable, #schedule-printable * { visibility: visible; }
          #schedule-printable { position: absolute; top: 0; left: 0; width: 100%; background: #fff; color: #000; padding: 20px; }
          .no-print { display: none !important; }
        }
      `}</style>

      {/* Controls bar (no-print) */}
      <div className="no-print" style={{
        display: "flex", justifyContent: "space-between", flexWrap: "wrap",
        gap: 10, marginBottom: 18,
      }}>
        <div>
          <h2 style={{ color: "#FFD700", margin: 0, fontFamily: "Georgia, serif" }}>
            My Class Schedule
          </h2>
          <p style={{ color: "#888", fontSize: "0.85rem", margin: "3px 0 0" }}>
            {isAdmin && targetUserId ? "Admin view · School-generated" : "Integrated K-12 · Academic Foundations + Zion Pillars"}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select
            data-testid="grade-selector"
            value={selectedGrade || data.current_grade}
            onChange={(e) => changeGrade(e.target.value)}
            style={{
              background: "#111", color: "#fff", border: "1px solid #444",
              padding: "8px 12px", borderRadius: 6, fontSize: "0.85rem",
            }}>
            {data.grade_order.map((g) => (
              <option key={g} value={g}>Grade {g === "K" ? "Kindergarten" : g}</option>
            ))}
          </select>
          <button data-testid="schedule-print-btn" onClick={handlePrint}
                  style={_btn("#87CEEB")}>🖨️ Print</button>
          <button data-testid="schedule-pdf-btn" onClick={handleDownloadPDF} disabled={busy}
                  style={_btn("#FFD700", busy)}>📄 Download PDF</button>
        </div>
      </div>

      {/* Ordination opt-in card */}
      <div className="no-print" style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 12, padding: "12px 16px", marginBottom: 16,
        background: ordinationStatus?.opted_in
          ? "linear-gradient(90deg,#2a1a3a 0%,#1a2a3a 100%)"
          : "#141420",
        border: `1px solid ${ordinationStatus?.opted_in ? "#DDA0DD" : "#333"}`,
        borderRadius: 10,
      }}>
        <div>
          <div style={{ color: "#DDA0DD", fontSize: "0.85rem", fontWeight: "bold" }}>
            ⛪ Ordination Track (Optional)
          </div>
          <div style={{ color: "#aaa", fontSize: "0.75rem", marginTop: 3 }}>
            {ordinationStatus?.opted_in
              ? "You're enrolled — ordination courses appear in your dashboard."
              : "Completely optional. Opt in any time to unlock seminary courses."}
          </div>
        </div>
        <button data-testid="ordination-toggle-btn" onClick={toggleOrdination}
                disabled={busy}
                style={_btn(ordinationStatus?.opted_in ? "#ff9080" : "#90EE90", busy)}>
          {ordinationStatus?.opted_in ? "Withdraw" : "Enroll"}
        </button>
      </div>

      {/* Test-out CTA */}
      {data.test_out_available && (
        <div className="no-print" style={{
          padding: "10px 14px", marginBottom: 14, background: "#1a2a1a",
          border: "1px solid #90EE9066", borderRadius: 8,
          color: "#90EE90", fontSize: "0.8rem",
        }}>
          🎯 <strong>One-time placement test-out available.</strong> Complete
          a short assessment to be placed above Kindergarten.
        </div>
      )}

      {/* Printable schedule */}
      <div id="schedule-printable" ref={printRef} style={{
        background: "#0a0a0f", border: "1px solid #333", borderRadius: 12,
        padding: 20,
      }}>
        <div style={{ borderBottom: "2px solid #FFD700", paddingBottom: 10, marginBottom: 16 }}>
          <div style={{ fontSize: "1.4rem", fontWeight: "bold", color: "#FFD700" }}>
            Yisrael Learning Center
          </div>
          <div style={{ fontSize: "0.9rem", color: "#ccc" }}>
            Weekly Schedule — Grade {data.target_grade === "K" ? "Kindergarten" : data.target_grade}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#888", marginTop: 2 }}>
            Student: {user?.username || "—"} · Generated {new Date().toLocaleDateString()}
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
            <thead>
              <tr style={{ background: "#1a1a2e" }}>
                <th style={_th}>Day</th>
                <th style={_th}>Sessions</th>
              </tr>
            </thead>
            <tbody>
              {data.schedule.week.map((row) => (
                <tr key={row.day} style={{ borderBottom: "1px solid #222" }}>
                  <td style={{ ..._td, verticalAlign: "top", fontWeight: "bold", color: "#FFD700" }}>
                    {row.day}
                  </td>
                  <td style={_td}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {row.sessions.map((s, i) => {
                        const c = TYPE_COLOR[s.type] || TYPE_COLOR.academic;
                        return (
                          <div key={i} style={{
                            display: "flex", justifyContent: "space-between",
                            gap: 12, padding: "6px 10px", borderRadius: 6,
                            background: c.bg, borderLeft: `3px solid ${c.border}`,
                          }}>
                            <div>
                              <div style={{ color: c.text, fontWeight: "bold" }}>
                                {s.subject}
                              </div>
                              <div style={{ color: "#888", fontSize: "0.7rem" }}>
                                {c.label}
                              </div>
                            </div>
                            <div style={{ color: "#ccc", whiteSpace: "nowrap", fontSize: "0.8rem" }}>
                              {s.time} · {s.minutes}min
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ ..._td, fontWeight: "bold", color: "#DDA0DD" }}>Saturday</td>
                <td style={{ ..._td, color: "#DDA0DD", fontStyle: "italic" }}>
                  Sabbath — Rest, no scheduled sessions.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{
          marginTop: 16, paddingTop: 10, borderTop: "1px solid #222",
          fontSize: "0.7rem", color: "#666",
        }}>
          Yisrael Learning Center · Integrated K-12 with Zion Pillars · Enochian 364-Day Calendar
        </div>
      </div>
    </div>
  );
}

const _btn = (color, disabled) => ({
  background: disabled ? "#222" : "transparent",
  color: disabled ? "#555" : color,
  border: `1px solid ${color}80`,
  borderRadius: 6, padding: "8px 14px",
  fontSize: "0.85rem", fontWeight: "bold",
  cursor: disabled ? "not-allowed" : "pointer",
  width: "auto", flex: "0 0 auto", whiteSpace: "nowrap",
});
const _th = { textAlign: "left", padding: "10px 12px", color: "#FFD700",
              fontSize: "0.8rem", borderBottom: "1px solid #333" };
const _td = { padding: "10px 12px", color: "#ddd" };
